import CardList, {
  type CardListRowTransformContext,
  type CardListRowRendererProps,
} from '@/components/CardList/CardList';
import { CardInteractive } from '@/components/Card/TableItemCard';
import {
  Button,
  CircularProgress,
  Tag,
  ToggleButton,
  Tooltip,
  Typography,
  useLocaleFormat,
} from '@eye-able/ui';
import { CircleSlash2, FileText, Pin, Target } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { currentAuditEntryIdAtom } from '@/global-store';
import { useAtom } from 'jotai/react';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { usePinAuditEntry, useUnpinAuditEntry } from '@/http/queries';
import { cn } from '@helpers/cn';
import { T } from '@/i18n/T';
import { getShortUrl } from '@helpers/shorten-url';
import { EditDomainAliasDialog } from '@components/Dialog/EditDomainAliasDialog';

export type ReportRow = {
  id: number;
  domain: string;
  domainAlias: string | null;
  isPinned: boolean;
  summaryDetailScore?: number | string;
  conformanceTarget?: string;
  lastTotalPagesChecked?: number;
  lastAvgErrorCount?: number;
};
export interface HomeReportTableProps {
  data: ReportRow[];
}

function HomeReportTable({ data }: HomeReportTableProps) {
  const { t } = useInlineTranslation();
  const columns = useColumns();
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);

  const onRowClick = useOnRowClick();

  const filteredData = useMemo(() => {
    if (!showOnlyFavorites) {
      return data;
    }

    return data.filter((row) => row.isPinned);
  }, [data, showOnlyFavorites]);

  return (
    <CardList
      data={filteredData}
      columns={columns}
      id='home-report-table'
      getRowId={(row, index) => row?.id ?? row?.domain ?? index}
      rowTitle={RowTitle}
      rowMain={RowMain}
      actions={
        <ToggleButton
          pressed={showOnlyFavorites}
          aria-label={t('favoritesOnly')}
          className='eye:bg-white'
          onPressedChange={setShowOnlyFavorites}
        >
          <Pin className='eye:stroke-[1.5]' aria-hidden />
        </ToggleButton>
      }
      rowTitleAction={RowTitleAction}
      transformRows={reorderPinnedRows}
      searchColumnId={['domain', 'domainAlias']}
      defaultSortBy='domain'
      defaultSortDirection='desc'
      onRowClick={onRowClick}
    />
  );
}

const RowTitleAction = ({ row }: CardListRowRendererProps<ReportRow>) => {
  const { t } = useInlineTranslation();
  const pinAuditEntry = usePinAuditEntry();
  const unpinAuditEntry = useUnpinAuditEntry();
  const label = row.isPinned ? t('removeFavorite') : t('addFavorite');

  const handleClick = () => {
    if (row.isPinned) {
      unpinAuditEntry.mutate({ auditEntryId: row.id });
    } else {
      pinAuditEntry.mutate({ auditEntryId: row.id });
    }
  };

  return (
    <div
      data-title-highlighted={row.isPinned || undefined}
      className='eye:flex eye:items-center eye:gap-4xs'
    >
      <EditDomainAliasDialog
        domain={row.domain}
        auditEntryId={row.id}
        domainAlias={row.domainAlias || ''}
      />
      <Tooltip content={label} delayDuration={1000}>
        <Button
          type='button'
          variant='ghost'
          size='icon-xs'
          disabled={pinAuditEntry.isPending || unpinAuditEntry.isPending}
          aria-label={label}
          onClick={handleClick}
        >
          <Pin
            aria-hidden
            className={cn({
              'eye:fill-current eye:text-blue-100': row.isPinned,
            })}
          />
        </Button>
      </Tooltip>
    </div>
  );
};

const StaticInfo = ({ row }: { row: ReportRow }) => {
  const { t } = useInlineTranslation();
  const { formatNumber } = useLocaleFormat();

  return (
    <CardInteractive className='eye:flex eye:flex-wrap eye:items-center eye:gap-2xs eye:body-s'>
      <Tooltip content={t('target')}>
        <div className='eye:flex eye:items-center eye:gap-5xs'>
          <Target
            aria-label={t('target')}
            className='eye:stroke-[1.5] eye:size-16'
          />
          <span>{getConformanceLevelString(row.conformanceTarget)}</span>
        </div>
      </Tooltip>
      <Tooltip content={t('numberOfPages')}>
        <div className='eye:flex eye:items-center eye:gap-5xs'>
          <FileText
            aria-label={t('numberOfPages')}
            className='eye:stroke-[1.5] eye:size-16'
          />
          <span>{row.lastTotalPagesChecked ?? 0}</span>
        </div>
      </Tooltip>
      <Tooltip content={t('averageErrorCount')}>
        <div className='eye:flex eye:items-center eye:gap-5xs'>
          <CircleSlash2
            aria-label={t('averageErrorCount')}
            className='eye:stroke-[1.5] eye:size-16'
          />
          <span>{formatNumber(row.lastAvgErrorCount ?? 0)}</span>
        </div>
      </Tooltip>
    </CardInteractive>
  );
};

const RowTitle = ({ row, view }: CardListRowRendererProps<ReportRow>) => {
  const title = row.domainAlias || getShortUrl(row.domain, 40);
  const subTitle = row.domainAlias ? getShortUrl(row.domain, 40) : undefined;

  if (view === 'grid') {
    return (
      <div className='eye:flex eye:flex-col eye:gap-5xs'>
        <div className='eye:overflow-hidden'>{title}</div>
        {subTitle && (
          <div className='eye:text-12 eye:overflow-hidden eye:text-grey-120'>
            {subTitle}
          </div>
        )}
        <StaticInfo row={row} />
      </div>
    );
  }

  return (
    <div>
      {title}
      <div className='eye:truncate eye:text-12 eye:overflow-hidden eye:text-grey-120'>
        {subTitle}
      </div>
    </div>
  );
};

const RowMain = ({ row, view }: CardListRowRendererProps<ReportRow>) => {
  const { t } = useInlineTranslation();

  const score = Math.min(
    Math.max(Number.parseFloat(String(row.summaryDetailScore)) || 0, 0),
    100,
  );

  if (view === 'grid') {
    return (
      <div className='eye:min-h-[140px] eye:p-s'>
        <Tooltip content={t('overallRating')}>
          <CardInteractive>
            <CircularProgress
              value={score}
              ariaLabel={t('overallRating')}
              size='default'
            />
          </CardInteractive>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className='eye:flex eye:w-full eye:justify-between'>
      <StaticInfo row={row} />

      <Tooltip content={t('overallRating')}>
        <CardInteractive>
          <CircularProgress
            value={score}
            ariaLabel={t('overallRating')}
            size='small'
          />
        </CardInteractive>
      </Tooltip>
    </div>
  );
};

function getConformanceLevelString(target?: string | null) {
  if (!target) return '';

  const levels = [];

  if (target.includes('AAA')) levels.push('AAA');
  else if (target.includes('AA')) levels.push('AA');
  else if (target.includes('A')) levels.push('A');

  if (target.includes('Best Practice')) levels.push('Best Practice');

  return levels.join(', ');
}

function useOnRowClick() {
  const navigate = useNavigate();
  const [, setCurrentAuditEntryId] = useAtom(currentAuditEntryIdAtom);

  return function onRowClick(row: ReportRow) {
    if (typeof row.id === 'number') {
      setCurrentAuditEntryId(row.id);
    }
    void navigate('/report/overview');
  };
}

function useColumns(): ColumnDef<ReportRow>[] {
  const { t } = useInlineTranslation();

  const columns: ColumnDef<ReportRow>[] = useMemo(() => {
    const parseConformanceTokens = (value?: string | null): string[] => {
      if (!value) return [];

      return value
        .split(/[+,]/)
        .map((token) => token.trim())
        .filter(Boolean);
    };
    return [
      {
        id: 'domain',
        accessorKey: 'domain',
        header: t('domain'),
        sortDescFirst: true,
        enableSorting: true,
        enableColumnFilter: true,
        sortingFn: (rowA, rowB) => {
          const labelA = rowA.original.domainAlias || rowA.original.domain;
          const labelB = rowB.original.domainAlias || rowB.original.domain;
          const primaryComparison = labelA.localeCompare(labelB);

          if (primaryComparison !== 0) {
            return primaryComparison;
          }
          // deterministic tie-breaks on domain and id so equal aliases don’t
          // produce unstable ordering
          const domainComparison = rowA.original.domain.localeCompare(
            rowB.original.domain,
          );

          if (domainComparison !== 0) {
            return domainComparison;
          }

          return rowA.original.id - rowB.original.id;
        },
        meta: {
          headerLabelRenderer: () => (
            <div className='eye:flex eye:items-center eye:gap-4xs eye:h-full'>
              <Typography>
                <T k='domain' />
              </Typography>
              <Typography variant='tag' className='eye:text-grey-120! eye:mt-1'>
                (<T k='favoritesOnTop' />)
              </Typography>
            </div>
          ),
          filterVariant: 'text',
        },
      },
      {
        id: 'domainAlias',
        accessorKey: 'domainAlias',
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'summaryDetailScore',
        accessorFn: (row) => {
          const value = row.summaryDetailScore;
          if (value === null || value === undefined || value === '') return 0;
          const parsed = Number.parseFloat(String(value));
          return Number.isNaN(parsed) ? 0 : parsed;
        },
        header: t('overallRating'),
        sortDescFirst: true,
        enableColumnFilter: false,
      },
      {
        id: 'conformanceTarget',
        accessorKey: 'conformanceTarget',
        header: t('target'),
        sortDescFirst: true,
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValues) => {
          if (!Array.isArray(filterValues) || filterValues.length === 0) {
            return true;
          }

          const cellValue = row.getValue<string | null | undefined>(columnId);
          const tokens = parseConformanceTokens(cellValue);

          return filterValues.every((selected) =>
            tokens.includes(String(selected)),
          );
        },
        meta: {
          filterVariant: 'multiselect',
          filterOptions: ['A', 'AA', 'AAA', 'Best Practice'],
          filterRenderer: (item: { value: string }) => <Tag>{item.value}</Tag>,
        },
      },
      {
        id: 'lastTotalPagesChecked',
        accessorKey: 'lastTotalPagesChecked',
        header: t('numberOfPages'),
        sortDescFirst: true,
        enableColumnFilter: true,
        meta: {
          filterVariant: 'range',
        },
      },
      {
        id: 'lastAvgErrorCount',
        accessorKey: 'lastAvgErrorCount',
        header: t('averageErrorCount'),
        sortDescFirst: true,
        enableColumnFilter: true,
        meta: {
          filterVariant: 'range',
        },
      },
    ] satisfies ColumnDef<ReportRow>[];
  }, [t]);

  return columns;
}

function isSortedByDomain({
  sorting,
}: Pick<CardListRowTransformContext, 'sorting'>) {
  if (sorting.length !== 1) {
    return false;
  }

  return sorting[0]?.id === 'domain';
}

function reorderPinnedRows(
  rows: ReportRow[],
  context: CardListRowTransformContext,
) {
  if (!isSortedByDomain(context)) {
    return rows;
  }

  const pinnedRows = [];
  const unpinnedRows = [];

  for (const row of rows) {
    if (row.isPinned) {
      pinnedRows.push(row);
      continue;
    }

    unpinnedRows.push(row);
  }

  return [...pinnedRows, ...unpinnedRows];
}

export default HomeReportTable;
